import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GoogleLoginButton from './GoogleLoginButton';
import AppleLoginButton from './AppleLoginButton';
import { RECRUITER_DEMO_URL } from '../../constants/recruiterContact';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  veeHelper?: React.ReactNode;
  signupPath?: string; // Custom signup path (default: /signup)
  hideSocialLogin?: boolean; // Hide Google/Apple buttons (e.g. for recruiter signup)
}

/**
 * Reusable Authentication Modal
 * 
 * Shows when user needs to sign in or sign up to access protected features.
 * After authentication, user is redirected back to the current page.
 */
const AuthModal: React.FC<AuthModalProps> = ({ 
  isOpen, 
  onClose,
  veeHelper,
  signupPath = '/signup', // Default to candidate signup (recruiter signup is invite-only via direct link)
  hideSocialLogin = false
}) => {
  const { t } = useTranslation('common');
  const location = useLocation();

  if (!isOpen) return null;

  // Store current path for return URL after auth
  const returnUrl = location.pathname + location.search;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] backdrop-blur-sm"
      onClick={(e) => {
        // Close modal when clicking on backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Container for modal - centered */}
        <div className="relative flex flex-col items-center justify-center">
        {/* Modal Content */}
        <div className="bg-white rounded-2xl p-8 sm:p-10 max-w-md w-full mx-4 shadow-2xl border border-gray-100">
          <div className="text-center">
            {/* Context-aware heading */}
            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
              {hideSocialLogin
                ? t('authModal.recruiterSignup', 'Sign up as Recruiter')
                : t('authModal.explorerSignup', 'Sign up as Explorer')}
            </h3>
            <p className="text-gray-700 text-sm font-semibold mb-5">
              ✨ {t('authModal.freeText', "It's free!")}
            </p>
            
            {!hideSocialLogin && (
              <>
                {/* Social Login (Primary - fastest path) */}
                <div className="space-y-3">
                  <GoogleLoginButton />
                  <AppleLoginButton />
                </div>
                
                {/* Terms text */}
                <p className="text-[9px] sm:text-[10px] text-gray-500 mt-3">
                  {t('signup.socialTermsText', 'By continuing, you agree to our')}{' '}
                  <Link to="/terms" className="underline hover:text-gray-700">{t('signup.termsLink')}</Link>
                  {' '}{t('signup.termsAnd')}{' '}
                  <Link to="/privacy" className="underline hover:text-gray-700">{t('signup.privacyLink')}</Link>
                  {t('signup.socialTermsSuffix', '')}
                </p>
                
                {/* Divider */}
                <div className="my-5">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">{t('authModal.or', 'or')}</span>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Sign Up with Email Button */}
            <Link to={`${signupPath}?return=${encodeURIComponent(returnUrl)}`}>
              <button className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white px-6 py-3 rounded-xl hover:from-pink-600 hover:to-rose-600 transition-all font-semibold shadow-sm text-sm">
                {t('authModal.signUpWithEmail', 'Sign up with Email')}
              </button>
            </Link>

            {/* Invited recruiter fallback - only show on explorer modals */}
            {!hideSocialLogin && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-[11px] text-gray-500">
                  {t('signup.recruiterInvitedIntro')}{' '}
                  <a
                    href={RECRUITER_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-pink-600 hover:text-pink-700 transition-colors"
                  >
                    {t('signup.recruiterInvitedCta')} →
                  </a>
                </p>
              </div>
            )}

            {/* Already have account - compact */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <p className="text-[11px] text-gray-400">{t('authModal.alreadyHaveAccount')}</p>
              <Link to={`/login?return=${encodeURIComponent(returnUrl)}`}>
                <button className="mt-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 transition-colors">
                  {t('authModal.signInButton')}
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;

